/**
 * HTTP document service — gateway `/documents` endpoints (proxied to
 * taxhive-document-service).
 *
 * Contract notes (verified against taxhive-document-service document_routes.py):
 *  - List:      `GET  /documents`            → `{ items, total, page, page_size }`,
 *               filters: status, category, file_type, linked_entity_id.
 *  - By entity: `GET  /documents/by-entity/{entity_type}/{entity_id}`.
 *  - Get one:   `GET  /documents/{id}`.
 *  - Upload:    `POST /documents/upload` — multipart `file` body; `category`,
 *               `linked_entity_type`, `linked_entity_id`, `description` are
 *               QUERY params. Tenant + uploader derived from the auth token.
 *  - Verify:    `PATCH /documents/{id}/verify` with `{ is_verified, verification_notes }`.
 *  - Search:    `GET  /documents/search/?q=...` (note the trailing slash; `q`
 *               must be ≥ 2 chars).
 *  - Versions:  `GET  /documents/{id}/versions` → `{ versions: [...] }`.
 *  - Download:  `GET  /documents/{id}/download` → `{ download_url, ... }`
 *               (a pre-signed URL, NOT the file binary).
 *  - Bulk:      `POST /documents/bulk-delete` / `POST /documents/bulk-verify`
 *               with `{ document_ids }`.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { DocumentService } from './documents.service'
import type {
  Document,
  DocumentVersion,
  DocumentListParams,
  UploadDocumentInput,
  DocumentEntityType,
} from '@/types/document.types'
import {
  mapDocumentFromApi,
  mapDocumentListFromApi,
  mapDocumentVersionListFromApi,
} from './mappers'

const DEFAULT_PAGE_SIZE = 100

/** Translate the camelCase list params into the backend's query string. */
function toListQuery(params?: DocumentListParams): Record<string, unknown> {
  const query: Record<string, unknown> = {
    page: params?.page ?? 1,
    page_size: params?.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  if (params?.status) query.status = params.status
  if (params?.category) query.category = params.category
  if (params?.fileType) query.file_type = params.fileType
  if (params?.linkedEntityId) query.linked_entity_id = params.linkedEntityId
  if (params?.assessmentYear) query.assessment_year = params.assessmentYear
  return query
}

export function createHttpDocumentService(): DocumentService {
  return {
    async list(params?: DocumentListParams): Promise<Document[]> {
      try {
        const res = await apiClient.get('/documents', { params: toListQuery(params) })
        return mapDocumentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listForEntity(
      entityType: DocumentEntityType,
      entityId: string
    ): Promise<Document[]> {
      try {
        const res = await apiClient.get(
          `/documents/by-entity/${entityType}/${entityId}`,
          { params: { page: 1, page_size: DEFAULT_PAGE_SIZE } }
        )
        return mapDocumentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async get(id: string): Promise<Document> {
      try {
        const res = await apiClient.get(`/documents/${id}`)
        return mapDocumentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async upload(input: UploadDocumentInput): Promise<Document> {
      try {
        // File goes in the multipart body; everything else is a query param.
        const form = new FormData()
        if (input.file) {
          form.append('file', input.file)
        } else {
          // No real binary (e.g. metadata-only flows) — send an empty
          // placeholder so the multipart `file` field is satisfied.
          form.append('file', new Blob([], { type: 'application/octet-stream' }), input.name ?? 'document')
        }

        const params: Record<string, unknown> = { category: input.category }
        if (input.linkedEntityType) params.linked_entity_type = input.linkedEntityType
        if (input.linkedEntityId) params.linked_entity_id = input.linkedEntityId
        if (input.description) params.description = input.description
        if (input.assessmentYear) params.assessment_year = input.assessmentYear
        if (input.financialYear) params.financial_year = input.financialYear
        if (input.workspaceService) params.workspace_service = input.workspaceService
        if (input.parentDocumentId) params.parent_document_id = input.parentDocumentId
        if (input.version) params.version = input.version

        const res = await apiClient.post('/documents/upload', form, {
          params,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        return mapDocumentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async remove(id: string, reason = 'User requested deletion'): Promise<void> {
      try {
        await apiClient.delete(`/documents/${id}`, { headers: { 'X-Deletion-Reason': reason } })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async verify(
      id: string,
      isVerified: boolean,
      notes?: string
    ): Promise<Partial<Document>> {
      try {
        const res = await apiClient.patch(`/documents/${id}/verify`, {
          is_verified: isVerified,
          verification_notes: notes ?? null,
        })
        return mapDocumentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async search(q: string): Promise<Document[]> {
      try {
        // Backend requires q ≥ 2 chars; short-circuit to avoid a 422.
        if (q.trim().length < 2) return []
        const res = await apiClient.get('/documents/search/', {
          params: { q, page: 1, page_size: DEFAULT_PAGE_SIZE },
        })
        return mapDocumentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async versions(id: string): Promise<DocumentVersion[]> {
      try {
        const res = await apiClient.get(`/documents/${id}/versions`)
        return mapDocumentVersionListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getDownloadUrl(id: string): Promise<string> {
      try {
        const res = await apiClient.get(`/documents/${id}/download`)
        return (res.data?.download_url ?? res.data?.downloadUrl ?? '') as string
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async bulkDelete(ids: string[]): Promise<void> {
      try {
        await apiClient.post('/documents/bulk-delete', { document_ids: ids })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async bulkVerify(ids: string[]): Promise<void> {
      try {
        await apiClient.post('/documents/bulk-verify', { document_ids: ids })
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
